import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Box,
  Typography,
  List,
  ListItem,
  Card,
  CardContent,
  Grid,
  IconButton,
  Divider,
  Tooltip,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import {
  getFiles,
  downloadFile,
  deleteSpecificFile,
} from "../../api/Design/fileApi";
import {
  getFilesByMuestraId,
  uploadFileToMuestra,
  deleteFileFromMuestra,
  downloadFileFromMuestra,
  moveTechnicalFileBetweenMuestras,
  moveDesignTechnicalToMuestra,
} from "../../api/Muestras/muestraApi";
import FileThumbnails from "../Estimates/FileThumbnails";
import {
  TECH_FOLDERS,
  buildRenamedFile,
  buildTechnicalUploadBaseName,
} from "./technicalFileUtils";
import { getMuestraFileCategory } from "../../utils/muestraFileCategories";

const TECH_DRAG_MIME = "application/x-crm-tech-file";

const sectionTitleSx = (bg) => ({
  fontWeight: 800,
  letterSpacing: 0.8,
  px: 1,
  py: 0.5,
  minHeight: 28,
  display: "inline-flex",
  alignItems: "center",
  boxSizing: "border-box",
  borderRadius: 2,
  border: "1px solid",
  borderColor: "divider",
  lineHeight: 1.2,
  background: bg,
});

const pastillaBaseSx = {
  width: "100%",
  height: "100%",
  minHeight: 148,
  boxSizing: "border-box",
  px: 1.5,
  py: 1.25,
  borderRadius: 3,
  display: "flex",
  flexDirection: "column",
  gap: 1,
};

export { TECH_DRAG_MIME, pastillaBaseSx };

const emptyFolderMap = () =>
  Object.fromEntries(TECH_FOLDERS.map((folder) => [folder, []]));

const flattenFolderMap = (folderMap) =>
  TECH_FOLDERS.flatMap((folder) =>
    (folderMap[folder] || []).map((file) => ({ ...file, folder })),
  );

const groupSampleTechFiles = (allFiles) => {
  const next = emptyFolderMap();
  for (const file of allFiles) {
    const folder = getMuestraFileCategory(file);
    if (folder && next[folder]) next[folder].push(file);
  }
  return next;
};

export default function TechnicalAttachmentsPanel({
  productId,
  productName,
  temporada,
  marca,
  designer,
  clientName = "",
  /** @deprecated single-sample mode; prefer `muestras` */
  muestraId = null,
  muestraName = "",
  /** When provided, render one pastilla per sample (all visible, DnD-ready) */
  muestras = null,
  selectedMuestraId = null,
  onSelectMuestra,
  /** If provided, renders paired grid: sample column | tech column (same row height) */
  renderSampleColumn = null,
  onFilesChange,
  refreshKey = 0,
}) {
  const theme = useTheme();

  const sampleList = useMemo(() => {
    if (Array.isArray(muestras) && muestras.length > 0) return muestras;
    if (muestraId) {
      return [
        {
          ID_MUESTRA: muestraId,
          MUESTRA: muestraName,
          NombreCliente: clientName,
        },
      ];
    }
    return [];
  }, [muestras, muestraId, muestraName, clientName]);

  const multiMode = sampleList.length > 0;
  const hasDesignContext = Boolean(
    productId && productName && temporada && marca && designer,
  );

  const [legacyFiles, setLegacyFiles] = useState(emptyFolderMap);
  const [sampleFilesMap, setSampleFilesMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [dragOverTarget, setDragOverTarget] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteCtx, setDeleteCtx] = useState(null);

  const loadLegacyDesignFiles = useCallback(async () => {
    if (!hasDesignContext) {
      setLegacyFiles(emptyFolderMap());
      return [];
    }
    const entries = await Promise.all(
      TECH_FOLDERS.map(async (folder) => {
        try {
          const fetched = await getFiles(
            temporada,
            marca,
            designer,
            productName,
            folder,
          );
          return [folder, Array.isArray(fetched) ? fetched : []];
        } catch {
          return [folder, []];
        }
      }),
    );
    const map = Object.fromEntries(entries);
    setLegacyFiles(map);
    return flattenFolderMap(map);
  }, [hasDesignContext, temporada, marca, designer, productName]);

  const loadSampleFiles = useCallback(async (list) => {
    if (!list.length) {
      setSampleFilesMap({});
      return {};
    }
    const pairs = await Promise.all(
      list.map(async (muestra) => {
        const id = muestra.ID_MUESTRA;
        try {
          const allFiles = await getFilesByMuestraId(id);
          const techOnly = allFiles.filter((file) =>
            TECH_FOLDERS.includes(getMuestraFileCategory(file)),
          );
          return [id, groupSampleTechFiles(techOnly)];
        } catch {
          return [id, emptyFolderMap()];
        }
      }),
    );
    const map = Object.fromEntries(pairs);
    setSampleFilesMap(map);
    return map;
  }, []);

  const sampleIdsKey = sampleList.map((m) => m.ID_MUESTRA).join(",");

  const loadAll = useCallback(async () => {
    if (!hasDesignContext && !multiMode) return;
    setLoading(true);
    try {
      const [legacyFlat, samplesMap] = await Promise.all([
        loadLegacyDesignFiles(),
        loadSampleFiles(sampleList),
      ]);
      const sampleCount = Object.values(samplesMap).reduce(
        (sum, folderMap) => sum + flattenFolderMap(folderMap).length,
        0,
      );
      onFilesChange?.(legacyFlat.length + sampleCount > 0);
    } catch (error) {
      console.error("Error loading technical files:", error);
    } finally {
      setLoading(false);
    }
  }, [
    hasDesignContext,
    multiMode,
    loadLegacyDesignFiles,
    loadSampleFiles,
    sampleList,
    onFilesChange,
  ]);

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, productName, temporada, marca, designer, refreshKey, sampleIdsKey]);

  const parseTechPayload = (dataTransfer) => {
    try {
      const raw =
        dataTransfer.getData(TECH_DRAG_MIME) ||
        dataTransfer.getData("text/plain");
      if (!raw) return null;
      const payload = JSON.parse(raw);
      if (!payload?.folder || !payload?.fileName) return null;
      if (payload.source !== "design" && payload.source !== "muestra") {
        return null;
      }
      return payload;
    } catch {
      return null;
    }
  };

  const handleTechDragStart = (e, file, source, sourceMuestraId = null) => {
    const payload = JSON.stringify({
      source,
      sourceMuestraId: sourceMuestraId ? Number(sourceMuestraId) : null,
      folder: file.folder,
      fileName: file.name,
    });
    e.dataTransfer.setData(TECH_DRAG_MIME, payload);
    e.dataTransfer.setData("text/plain", payload);
    e.dataTransfer.effectAllowed = "move";
  };

  const moveToSample = async (targetMuestra, dataTransfer) => {
    const payload = parseTechPayload(dataTransfer);
    if (!payload || !targetMuestra?.ID_MUESTRA) return false;

    try {
      if (payload.source === "design") {
        await moveDesignTechnicalToMuestra(targetMuestra.ID_MUESTRA, {
          folder: payload.folder,
          fileName: payload.fileName,
        });
        await loadAll();
        return true;
      }

      if (payload.source === "muestra") {
        if (
          Number(payload.sourceMuestraId) === Number(targetMuestra.ID_MUESTRA)
        ) {
          return false;
        }
        await moveTechnicalFileBetweenMuestras(payload.sourceMuestraId, {
          targetMuestraId: targetMuestra.ID_MUESTRA,
          folder: payload.folder,
          fileName: payload.fileName,
        });
        await loadAll();
        return true;
      }
    } catch (error) {
      console.error("Error moviendo adjunto técnico:", error);
    }
    return false;
  };

  const getUploadBaseName = (muestra) =>
    buildTechnicalUploadBaseName({
      clientName: muestra?.NombreCliente || clientName,
      designName: muestra?.MUESTRA || productName,
    });

  const handleUploadToSample = async (eventOrFiles, folder, muestra) => {
    let selectedFiles = [];
    if (eventOrFiles?.dataTransfer) {
      selectedFiles = Array.from(eventOrFiles.dataTransfer.files || []);
      eventOrFiles.preventDefault();
    } else if (eventOrFiles?.target?.files) {
      selectedFiles = Array.from(eventOrFiles.target.files || []);
    }

    if (!selectedFiles.length || !muestra?.ID_MUESTRA) return;

    try {
      let existing = flattenFolderMap(
        sampleFilesMap[muestra.ID_MUESTRA] || emptyFolderMap(),
      ).filter((f) => f.folder === folder);

      for (const file of selectedFiles) {
        const renamed = buildRenamedFile(
          file,
          getUploadBaseName(muestra),
          existing,
        );
        if (!renamed) continue;
        await uploadFileToMuestra(muestra.ID_MUESTRA, renamed, folder);
        existing = [...existing, { name: renamed.name, folder }];
      }
      await loadAll();
    } catch (error) {
      console.error("Error uploading technical file:", error);
    }
  };

  const handleDelete = async () => {
    if (!deleteCtx) return;
    const { file, folder, scope, muestraId: delMuestraId } = deleteCtx;
    try {
      if (scope === "muestra" && delMuestraId) {
        await deleteFileFromMuestra(delMuestraId, file.name, folder);
      } else {
        await deleteSpecificFile(
          temporada,
          marca,
          designer,
          productName,
          folder,
          file.name,
        );
      }
      await loadAll();
    } catch (error) {
      console.error("Error deleting technical file:", error);
    } finally {
      setOpenDeleteDialog(false);
      setDeleteCtx(null);
    }
  };

  const handleDownload = async (file, scope, sid) => {
    try {
      if (scope === "muestra" && sid) {
        await downloadFileFromMuestra(sid, file.name, file.folder);
      } else {
        downloadFile(
          temporada,
          marca,
          designer,
          productName,
          file.folder,
          file.name,
        );
      }
    } catch (error) {
      console.error("Error downloading technical file:", error);
    }
  };

  const renderFileRow = (file, index, scope, sourceMuestraId = null) => (
    <ListItem
      key={`${scope}-${sourceMuestraId || "design"}-${file.folder}-${file.name}-${index}`}
      draggable
      onDragStart={(e) =>
        handleTechDragStart(e, file, scope, sourceMuestraId)
      }
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 1,
        px: 0.5,
        py: 0.75,
        cursor: "grab",
        borderRadius: 1,
        "&:active": { cursor: "grabbing" },
        "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.04) },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          minWidth: 0,
          flex: 1,
        }}
      >
        <FileThumbnails filePath={file.path} />
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{ fontWeight: 600, fontSize: ".85em", wordBreak: "break-word" }}
          >
            {file.name}
          </Typography>
          <Typography sx={{ fontSize: ".65em", color: "text.secondary" }}>
            {file.folder} · arrastra a una muestra
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
        <Tooltip title="Download">
          <IconButton
            size="small"
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              p: "2px",
            }}
            onClick={() => handleDownload(file, scope, sourceMuestraId)}
          >
            <DownloadIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton
            size="small"
            sx={{
              border: "1px solid",
              borderColor: alpha(theme.palette.error.main, 0.5),
              color: "error.main",
              borderRadius: 1,
              p: "2px",
            }}
            onClick={() => {
              setDeleteCtx({
                file,
                folder: file.folder,
                scope,
                muestraId: sourceMuestraId,
              });
              setOpenDeleteDialog(true);
            }}
          >
            <DeleteOutlinedIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      </Box>
    </ListItem>
  );

  const renderUploadButtons = (keyPrefix, onUpload, dropKey) => (
    <Box sx={{ mt: "auto", pt: 0.5 }}>
      <Typography sx={{ fontSize: ".75em", mb: 0.5, color: "text.secondary" }}>
        Upload to:
      </Typography>
      <Grid container spacing={0.75}>
        {TECH_FOLDERS.map((folder) => {
          const inputId = `upload-tech-${keyPrefix}-${folder}`;
          const isOver = dragOverTarget === `${dropKey}::${folder}`;
          return (
            <Grid item key={folder}>
              <input
                accept="*/*"
                style={{ display: "none" }}
                id={inputId}
                type="file"
                multiple
                onChange={(e) => onUpload(e, folder)}
              />
              <label htmlFor={inputId}>
                <Button
                  variant="outlined"
                  size="small"
                  component="span"
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const types = Array.from(e.dataTransfer?.types || []);
                    if (types.includes("Files")) {
                      setDragOverTarget(`${dropKey}::${folder}`);
                    }
                  }}
                  onDragLeave={() => setDragOverTarget(null)}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDragOverTarget(null);
                    const types = Array.from(e.dataTransfer?.types || []);
                    if (!types.includes("Files")) return;
                    onUpload(e, folder);
                  }}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontSize: ".75em",
                    borderStyle: isOver ? "dashed" : "solid",
                    backgroundColor: isOver
                      ? alpha(theme.palette.primary.main, 0.1)
                      : "transparent",
                    borderColor: isOver ? "primary.main" : "divider",
                  }}
                >
                  {folder}
                </Button>
              </label>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );

  const renderSamplePastilla = (muestra) => {
    const id = muestra.ID_MUESTRA;
    const folderMap = sampleFilesMap[id] || emptyFolderMap();
    const files = flattenFolderMap(folderMap);
    const dropKey = `sample-${id}`;
    const isDropTarget = dragOverTarget === dropKey;
    const isSelected = Number(selectedMuestraId) === Number(id);

    return (
      <Box
        key={id}
        onClick={() => onSelectMuestra?.(id)}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          const types = Array.from(e.dataTransfer?.types || []);
          const isFileDrop = types.includes("Files");
          const isTechMove =
            !isFileDrop &&
            (types.includes(TECH_DRAG_MIME) || types.includes("text/plain"));
          if (isTechMove) {
            setDragOverTarget(dropKey);
            e.dataTransfer.dropEffect = "move";
          }
        }}
        onDragLeave={() => {
          setDragOverTarget((prev) => (prev === dropKey ? null : prev));
        }}
        onDrop={async (e) => {
          e.preventDefault();
          e.stopPropagation();
          setDragOverTarget(null);
          onSelectMuestra?.(id);
          const moved = await moveToSample(muestra, e.dataTransfer);
          if (moved) return;
        }}
        sx={{
          ...pastillaBaseSx,
          cursor: onSelectMuestra ? "pointer" : "default",
          border: "1px solid",
          borderColor: isDropTarget
            ? "primary.main"
            : isSelected
              ? "primary.main"
              : "divider",
          borderStyle: isDropTarget ? "dashed" : "solid",
          backgroundColor: isDropTarget
            ? alpha(theme.palette.primary.main, 0.12)
            : isSelected
              ? alpha(theme.palette.primary.main, 0.08)
              : alpha(theme.palette.background.default, 0.5),
          boxShadow: isSelected
            ? `0 0 0 1px ${alpha(theme.palette.primary.main, 0.35)}`
            : "none",
          transition: "all 0.15s ease",
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: ".9em" }}>
          {muestra.MUESTRA}
        </Typography>
        {muestra.NombreCliente ? (
          <Typography sx={{ fontSize: ".7em", color: "text.secondary", mb: 0.5 }}>
            {muestra.NombreCliente}
          </Typography>
        ) : null}

        <List disablePadding sx={{ flex: 1 }}>
          {files.length === 0 ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", py: 0.5 }}
            >
              Sin adjuntos técnicos. Suelta aquí o sube abajo.
            </Typography>
          ) : (
            files.map((file, index) =>
              renderFileRow(file, index, "muestra", id),
            )
          )}
        </List>

        {renderUploadButtons(
          `muestra-${id}`,
          (e, folder) => handleUploadToSample(e, folder, muestra),
          dropKey,
        )}
      </Box>
    );
  };

  const legacyFlat = flattenFolderMap(legacyFiles);
  const showLegacy = hasDesignContext && legacyFlat.length > 0;

  if (!hasDesignContext && !multiMode) {
    return (
      <Card sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3 }}>
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            No se pudo cargar el contexto del diseño.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const paired = typeof renderSampleColumn === "function";

  const samplesTitle = (
    <Typography
      variant="overline"
      sx={{
        ...sectionTitleSx(
          alpha(theme.palette.text.quinto || "#ffac7e", 0.2),
        ),
        color: "text.secondary",
      }}
    >
      Samples
    </Typography>
  );

  const techTitle = (
    <Typography
      variant="overline"
      sx={sectionTitleSx(
        "linear-gradient(135deg, rgba(0,188,212,0.14) 0%, rgba(103,58,183,0.12) 100%)",
      )}
    >
      Technical Attachments
    </Typography>
  );

  return (
    <>
      <Card
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          boxShadow:
            theme.palette.mode === "dark"
              ? "0 4px 20px rgba(0,0,0,0.25)"
              : "0 6px 24px rgba(0,0,0,0.06)",
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <CardContent
          sx={{
            px: { xs: 1.5, sm: 2 },
            pt: 2,
            pb: 2,
            "&:last-child": { pb: 2 },
            overflowY: "auto",
            maxHeight: { md: "calc(100vh - 220px)" },
            boxSizing: "border-box",
          }}
        >
          {paired ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                columnGap: { xs: 0, md: 3 },
                rowGap: 1.5,
                alignItems: "stretch",
                width: "100%",
              }}
            >
              <Box>
                {samplesTitle}
                <Divider sx={{ mt: 1, mb: 0 }} />
              </Box>
              <Box>
                {techTitle}
                <Divider sx={{ mt: 1, mb: 0 }} />
              </Box>

              {loading ? (
                <Box
                  sx={{
                    gridColumn: "1 / -1",
                    display: "flex",
                    justifyContent: "center",
                    py: 2,
                  }}
                >
                  <CircularProgress size={28} />
                </Box>
              ) : null}

              {showLegacy ? (
                <Box
                  sx={{
                    gridColumn: "1 / -1",
                    px: 1.25,
                    py: 1.25,
                    borderRadius: 3,
                    border: "1px dashed",
                    borderColor: "warning.main",
                    backgroundColor: alpha(theme.palette.warning.main, 0.06),
                  }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: ".85em" }}>
                    Diseño (legacy)
                  </Typography>
                  <Typography
                    sx={{ fontSize: ".7em", color: "text.secondary", mb: 0.5 }}
                  >
                    Ficheros a nivel diseño. Arrástralos a una pastilla de
                    muestra.
                  </Typography>
                  <List disablePadding>
                    {legacyFlat.map((file, index) =>
                      renderFileRow(file, index, "design", null),
                    )}
                  </List>
                </Box>
              ) : null}

              {sampleList.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ gridColumn: "1 / -1" }}
                >
                  No hay muestras registradas.
                </Typography>
              ) : (
                sampleList.map((muestra) => (
                  <React.Fragment key={muestra.ID_MUESTRA}>
                    <Box sx={{ minWidth: 0, display: "flex" }}>
                      {renderSampleColumn(muestra)}
                    </Box>
                    <Box sx={{ minWidth: 0, display: "flex" }}>
                      {renderSamplePastilla(muestra)}
                    </Box>
                  </React.Fragment>
                ))
              )}
            </Box>
          ) : (
            <>
              {techTitle}
              <Divider sx={{ my: 1 }} />

              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : null}

              {showLegacy ? (
                <Box
                  sx={{
                    px: 1.25,
                    py: 1.25,
                    mb: 1.5,
                    borderRadius: 3,
                    border: "1px dashed",
                    borderColor: "warning.main",
                    backgroundColor: alpha(theme.palette.warning.main, 0.06),
                  }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: ".85em" }}>
                    Diseño (legacy)
                  </Typography>
                  <Typography
                    sx={{ fontSize: ".7em", color: "text.secondary", mb: 0.5 }}
                  >
                    Ficheros a nivel diseño. Arrástralos a una pastilla de
                    muestra.
                  </Typography>
                  <List disablePadding>
                    {legacyFlat.map((file, index) =>
                      renderFileRow(file, index, "design", null),
                    )}
                  </List>
                </Box>
              ) : null}

              {sampleList.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No hay muestras. Crea una muestra para colgar adjuntos
                  técnicos.
                </Typography>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {sampleList.map((muestra) => renderSamplePastilla(muestra))}
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={openDeleteDialog}
        onClose={() => {
          setOpenDeleteDialog(false);
          setDeleteCtx(null);
        }}
      >
        <DialogTitle>Confirm Deletion?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the file{" "}
            <strong>{deleteCtx?.file?.name}</strong>? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenDeleteDialog(false);
              setDeleteCtx(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            autoFocus
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
